import React from "react";
import { useReveal } from "../../hooks/useReveal";
import { Ornament, StarOfIshtar, CuneiformMotif } from "../Ornaments";

const Founder = ({ photo, alt, name, title, kicker, bio, testid }) => {
  return (
    <div data-testid={testid} style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <div className="sigil" data-testid={`${testid}-portrait`}>
        <div style={{ position: "absolute", top: 18, left: 18, opacity: 0.55, zIndex: 3 }}>
          <CuneiformMotif width={60} />
        </div>
        <div style={{ position: "absolute", bottom: 18, right: 18, opacity: 0.55, transform: "rotate(180deg)", zIndex: 3 }}>
          <CuneiformMotif width={60} />
        </div>
        <img
          src={photo}
          alt={alt}
          loading="lazy"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
            filter: "saturate(0.85) contrast(1.05) brightness(0.92)",
            zIndex: 1,
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(42,74,122,0.10) 0%, rgba(10,8,6,0.05) 40%, rgba(10,8,6,0.85) 100%)",
            zIndex: 2,
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            boxShadow: "inset 0 0 80px rgba(201, 168, 76, 0.18), inset 0 -40px 60px rgba(42, 74, 122, 0.18)",
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
      </div>
      <div>
        <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.7rem", letterSpacing: "0.34em", marginBottom: "0.55rem" }}>
          {title}
        </p>
        <h3 className="font-display" style={{ color: "var(--text)", fontSize: "1.55rem", letterSpacing: "0.04em", marginBottom: "0.4rem" }}>
          {name}
        </h3>
        {kicker && (
          <p className="font-body" style={{ color: "var(--gold-bright)", fontStyle: "italic", fontSize: "0.95rem", marginBottom: "1.2rem" }}>
            {kicker}
          </p>
        )}
        <p className="font-body" style={{ color: "var(--text-muted)", fontSize: "0.98rem", lineHeight: 1.85 }}>
          {bio}
        </p>
      </div>
    </div>
  );
};

const PHILLIP_PHOTO = "/founders/phillip.jpg";
const BENJAMIN_PHOTO = "/founders/benjamin.jpg";

export const Alliance = () => {
  const head = useReveal();
  const left = useReveal();
  const right = useReveal();
  const closing = useReveal();
  return (
    <section
      id="alliance"
      className="section bg-elevated-section"
      data-testid="alliance-section"
      style={{ position: "relative" }}
    >
      <div className="lapis-glow" style={{ width: 800, height: 800, top: 100, left: -300 }} />
      <div className="gold-glow" style={{ width: 600, height: 600, bottom: 200, right: -200 }} />

      <div className="section-inner">
        <div ref={head.ref} className={`reveal ${head.inView ? "in-view" : ""}`} style={{ textAlign: "center", maxWidth: 780, margin: "0 auto 4rem" }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Ornament centered />
          </div>
          <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.7rem", letterSpacing: "0.4em", marginBottom: "1rem" }}>
            The Alliance
          </p>
          <h2 className="section-title" data-testid="alliance-title">
            Two Lineages. One Table.
          </h2>
        </div>

        <div className="alliance-grid">
          <div ref={left.ref} className={`reveal ${left.inView ? "in-view" : ""}`}>
            <Founder
              testid="founder-phillip"
              photo={PHILLIP_PHOTO}
              alt="Phillip Obi Schlachter — Somatic Educator & Mental Health Clinician"
              name="Phillip Obi Schlachter"
              title="Somatic Educator & Mental Health Clinician"
              kicker="Founder, Seeded Somatics · Portland, Oregon"
              bio={
                <>
                  Over two decades in youth-serving and mental health settings. MA in Counseling
                  &amp; Psychology. His practice is rooted in the body as landscape — where fascia,
                  nervous system regulation, and ecological metaphor converge into a framework for
                  lasting reorganization. <span style={{ color: "var(--gold-bright)" }}>He brings the psychological architecture and the clinical depth.</span>
                </>
              }
            />
          </div>

          <div className="alliance-divider" data-testid="alliance-star-divider">
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1.5rem" }}>
              <div style={{ width: 1, height: 80, background: "linear-gradient(180deg, transparent, var(--gold), transparent)" }} className="hidden md:block" />
              <StarOfIshtar size={64} opacity={0.9} />
              <div style={{ width: 1, height: 80, background: "linear-gradient(180deg, var(--gold), transparent)" }} className="hidden md:block" />
            </div>
          </div>

          <div ref={right.ref} className={`reveal ${right.inView ? "in-view" : ""}`}>
            <Founder
              testid="founder-benjamin"
              photo={BENJAMIN_PHOTO}
              alt="Benjamin D'Aguilar — Movement Innovator, Brain & Body"
              name="Benjamin D'Aguilar"
              title="Movement Innovator · Brain & Body"
              kicker="Creator, MoveMinIt · Calgary, Canada"
              bio={
                <>
                  Former professional athlete who rebuilt himself from the inside out after
                  conventional medicine offered no answers. Pioneer of a self-developed method
                  that maps the inner and outer grid through movement — taking people not just
                  back to baseline but into conscious, embodied self-authorship. <span style={{ color: "var(--gold-bright)" }}>He brings the lived proof.</span>
                </>
              }
            />
          </div>
        </div>

        <div
          ref={closing.ref}
          className={`reveal ${closing.inView ? "in-view" : ""}`}
          style={{ textAlign: "center", marginTop: "5rem" }}
          data-testid="alliance-closing"
        >
          <p
            className="italic-quote"
            style={{
              color: "var(--gold-bright)",
              fontSize: "clamp(1.1rem, 1.7vw, 1.4rem)",
              letterSpacing: "0.04em",
              maxWidth: 640,
              margin: "0 auto",
              lineHeight: 1.6,
            }}
          >
            &ldquo;What neither could build alone, they build together.&rdquo;
          </p>
        </div>
      </div>
    </section>
  );
};
